import type { GridReadyEvent } from '@cmg/data-grid';
import { DataGridContextProvider, exportMultipleSheetsAsExcel } from '@cmg/data-grid';
import { SnackbarManager } from '@cmg/design-system';
import { format } from 'date-fns';
import isNil from 'lodash/isNil';
import { createContext, FC, useCallback, useEffect, useMemo, useState } from 'react';

import { CalendarGrid } from '../components/calendar-grid/CalendarGrid';
import type { CalendarGridRowData } from '../components/calendar-grid/CalendarGrid.model';
import { CALENDAR_TAB_LABELS } from '../components/calendar-tabs/CalendarTabs.model';
import { useGetCalendarGridProps } from '../hooks/useGetCalendarGridProps';
import { getColumnsToExport, processHeaderCallback } from './ExportCalendarGridContext.model';

export type ExportCalendarGridContextModel = {
  isExporting: boolean;
  handleExport: () => void;
};

type SpreadSheet = () => string | undefined;

export const ExportCalendarGridContext = createContext<ExportCalendarGridContextModel>({
  isExporting: false,
  handleExport: () => {},
});

/**
 * Context provider that handles the export of multiple calendar grids as a single Excel file with multiple data sheets.
 *
 * 1. Export is initiated by calling `handleExport` method
 * 2. Each calendar grid is rendered in a hidden div and fed with relevant row data and grid props
 * 3. When all grids renders and data loads, grid rows are exported as Excel sheets using `exportMultipleSheetsAsExcel` API
 *
 * https://www.ag-grid.com/react-data-grid/excel-export-multiple-sheets/#multiple-grids-to-multiple-sheets
 */
export const ExportCalendarGridProvider: FC = ({ children }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [spreadSheets, setSpreadSheets] = useState<SpreadSheet[]>([]);

  const { fetchGridRowData: fetchLiveCalendarGridRowData, ...liveCalendarGridProps } =
    useGetCalendarGridProps({ tab: 'live', withGranularPriceRangeColumns: true });

  const { fetchGridRowData: fetchPricedCalendarGridRowData, ...pricedCalendarGridProps } =
    useGetCalendarGridProps({ tab: 'priced', withGranularPriceRangeColumns: true });

  const { fetchGridRowData: fetchFiledCalendarGridRowData, ...filedCalendarGridProps } =
    useGetCalendarGridProps({ tab: 'filed', withGranularPriceRangeColumns: true });

  const { fetchGridRowData: fetchPostponedCalendarGridRowData, ...postponedCalendarGridProps } =
    useGetCalendarGridProps({ tab: 'postponed', withGranularPriceRangeColumns: true });

  const {
    fetchGridRowData: fetchLockupExpirationsCalendarGridRowData,
    ...lockupExpirationsCalendarGridProps
  } = useGetCalendarGridProps({ tab: 'lockupExpirations', withGranularPriceRangeColumns: true });

  const { fetchGridRowData: fetchMyOfferingsCalendarGridRowData, ...myOfferingsCalendarGridProps } =
    useGetCalendarGridProps({ tab: 'myOfferings', withGranularPriceRangeColumns: true });

  const exportableGrids = useMemo(() => {
    return [
      { name: CALENDAR_TAB_LABELS['live'], props: liveCalendarGridProps },
      { name: CALENDAR_TAB_LABELS['priced'], props: pricedCalendarGridProps },
      { name: CALENDAR_TAB_LABELS['filed'], props: filedCalendarGridProps },
      { name: CALENDAR_TAB_LABELS['postponed'], props: postponedCalendarGridProps },
      { name: CALENDAR_TAB_LABELS['lockupExpirations'], props: lockupExpirationsCalendarGridProps },
      { name: CALENDAR_TAB_LABELS['myOfferings'], props: myOfferingsCalendarGridProps },
    ];
  }, [
    filedCalendarGridProps,
    liveCalendarGridProps,
    lockupExpirationsCalendarGridProps,
    myOfferingsCalendarGridProps,
    postponedCalendarGridProps,
    pricedCalendarGridProps,
  ]);

  const isAllGridsRendered = spreadSheets.length === exportableGrids.length;
  const isAllDataLoaded = exportableGrids.every(({ props }) => props.isAllDataLoaded);
  const hasError = exportableGrids.some(({ props }) => !!props.error);

  const canExportSheetData = useMemo(() => {
    return isExporting && isAllDataLoaded && isAllGridsRendered && !hasError;
  }, [isAllDataLoaded, isAllGridsRendered, hasError, isExporting]);

  const stopExporting = useCallback(() => {
    setIsExporting(false);
    setSpreadSheets([]);
  }, []);

  const handleExport = useCallback(() => {
    setIsExporting(true);

    /**
     * Data are paginated and fetched in chunks, this initiates the fetching process.
     * Potential errors are handled in the useEffect hook below as they can emerge on subsequent requests.
     */
    void fetchLiveCalendarGridRowData();
    void fetchPricedCalendarGridRowData();
    void fetchFiledCalendarGridRowData();
    void fetchPostponedCalendarGridRowData();
    void fetchLockupExpirationsCalendarGridRowData();
    void fetchMyOfferingsCalendarGridRowData();
  }, [
    fetchFiledCalendarGridRowData,
    fetchLiveCalendarGridRowData,
    fetchLockupExpirationsCalendarGridRowData,
    fetchMyOfferingsCalendarGridRowData,
    fetchPostponedCalendarGridRowData,
    fetchPricedCalendarGridRowData,
  ]);

  const handleGridReady = useCallback(
    (name: string) => (event: GridReadyEvent<CalendarGridRowData>) => {
      const visibleColIds = event.api.getAllDisplayedColumns().map(column => column.getColId());

      setSpreadSheets(curr => {
        return [
          ...curr,
          () =>
            event.api.getSheetDataForExcel({
              sheetName: name,
              skipRowGroups: true,
              processHeaderCallback,
              columnKeys: getColumnsToExport(visibleColIds),
            }),
        ];
      });
    },
    []
  );

  useEffect(() => {
    if (!canExportSheetData) {
      return;
    }

    exportMultipleSheetsAsExcel({
      fileName: `CalendarOfferings_${format(Date.now(), 'yyyy-MM-dd')}.xlsx`,
      data: spreadSheets
        .map(getSheetData => getSheetData())
        .filter((data): data is string => !isNil(data)),
    });

    stopExporting();
  }, [canExportSheetData, spreadSheets, stopExporting]);

  useEffect(() => {
    if (isExporting && hasError) {
      SnackbarManager.error('Error exporting calendar data');
      stopExporting();
    }
  }, [hasError, stopExporting, isExporting]);

  return (
    <ExportCalendarGridContext.Provider value={{ isExporting, handleExport }}>
      {children}
      {isExporting && (
        <div hidden={true}>
          {exportableGrids.map(({ name, props }) => (
            <DataGridContextProvider key={name}>
              <CalendarGrid {...props} onGridReady={handleGridReady(name)} />
            </DataGridContextProvider>
          ))}
        </div>
      )}
    </ExportCalendarGridContext.Provider>
  );
};
